import { useState } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Shield, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VerifyOTP() {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  // Redirect if no email in state
  if (!email) {
    navigate('/forgot-password');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/verify-otp`, { email, otp });
      
      toast.success('OTP verified successfully!');
      
      // Navigate to reset password page
      navigate('/reset-password', { state: { email, otp } });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResending(true);

    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success('New OTP sent to your email!');
      setOtp('');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-8 py-20">
        <div className="max-w-md mx-auto bg-white border border-border rounded-none p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-black uppercase mb-2">VERIFY OTP</h1>
            <p className="text-sm text-muted-foreground mb-2">
              Enter the 6-digit code sent to
            </p>
            <p className="text-sm font-bold mono">{email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="uppercase tracking-wider text-xs font-bold">6-Digit Code</Label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                }}
                required
                placeholder="000000"
                maxLength={6}
                disabled={loading}
                className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-4 text-center text-3xl tracking-widest mono font-bold"
              />
              <p className="text-xs text-center text-muted-foreground mt-2">
                Valid for 10 minutes
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider font-bold py-6"
            >
              {loading ? 'VERIFYING...' : 'VERIFY OTP'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">Didn't receive the code?</p>
            <Button
              onClick={handleResendOTP}
              disabled={resending}
              variant="outline"
              className="rounded-none uppercase tracking-wider text-xs font-bold"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'SENDING...' : 'RESEND OTP'}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <Link 
              to="/forgot-password" 
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-accent transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
          </div>

          <div className="mt-8 p-4 bg-accent/10 border border-accent rounded-none">
            <p className="text-xs text-center text-muted-foreground">
              🔒 For your security, the OTP expires after 10 minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
